import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { LANDING_VIDEOS } from '@/config/assets'

interface StepCardProps {
  stepNumber: number
  videoSrc: string
  title: string
  description: string
}

function StepCard({ stepNumber, videoSrc, title, description }: StepCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-2xl bg-white p-6 text-center',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-1 hover:shadow-xl'
      )}
    >
      {/* Badge */}
      <div className="mb-4 flex h-8 w-12 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
        {String(stepNumber).padStart(2, '0')}
      </div>

      {/* Video */}
      <div className="mb-4 h-20 w-20 overflow-hidden rounded-xl border-2 border-dashed border-border bg-white">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="h-full w-full object-cover"
        >
          <source src={videoSrc} type="video/webm" />
        </video>
      </div>

      {/* Title */}
      <h3 className="mb-2 text-lg font-bold text-foreground">{title}</h3>

      {/* Description */}
      <p className="whitespace-pre-line text-sm leading-relaxed text-foreground-muted">
        {description}
      </p>
    </div>
  )
}

function ChevronConnector() {
  return (
    <div className="hidden items-center justify-center lg:flex">
      <ChevronRight
        className={cn(
          'h-8 w-8 text-accent',
          'animate-pulse-right'
        )}
      />
    </div>
  )
}

/**
 * How It Works section with 4 step cards
 * Implementation: LAND2-002
 */
export function HowItWorksSection() {
  const { t } = useTranslation()

  const steps = [
    {
      stepNumber: 1,
      videoSrc: LANDING_VIDEOS.steps.shareVision,
      titleKey: 'landing.how.step1.title',
      descKey: 'landing.how.step1.desc',
    },
    {
      stepNumber: 2,
      videoSrc: LANDING_VIDEOS.steps.getChallenged,
      titleKey: 'landing.how.step2.title',
      descKey: 'landing.how.step2.desc',
    },
    {
      stepNumber: 3,
      videoSrc: LANDING_VIDEOS.steps.watchItForm,
      titleKey: 'landing.how.step3.title',
      descKey: 'landing.how.step3.desc',
    },
    {
      stepNumber: 4,
      videoSrc: LANDING_VIDEOS.steps.ownYourSpecs,
      titleKey: 'landing.how.step4.title',
      descKey: 'landing.how.step4.desc',
    },
  ]

  return (
    <div className="bg-primary py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center lg:mb-16">
          <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            {t('landing.how.title')}
          </h2>
          <p className="mt-4 text-lg text-white/70 sm:text-xl">
            {t('landing.how.subtitle')}
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-center lg:gap-4">
          {steps.map((step, index) => (
            <div key={step.stepNumber} className="contents">
              <StepCard
                stepNumber={step.stepNumber}
                videoSrc={step.videoSrc}
                title={t(step.titleKey)}
                description={t(step.descKey)}
              />
              {index < steps.length - 1 && <ChevronConnector />}
            </div>
          ))}
        </div>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse-right {
          0%, 100% {
            transform: translateX(0);
            opacity: 0.6;
          }
          50% {
            transform: translateX(4px);
            opacity: 1;
          }
        }
        .animate-pulse-right {
          animation: pulse-right 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
